import { externalApi } from '../../shared/config/axiosConfig';

/**
 * @typedef {object} BrowserAccessToken
 * @property {string} accessToken current browser access token
 * @property {number} [expiresIn] access-token lifetime in seconds
 * @property {string} [tokenType] token scheme
 * @property {import('../../../global').UserDataResponse} [user] authenticated user
 * @property {number} [userId] authenticated user identifier
 * @property {string} [role] authenticated user role
 */

/**
 * @typedef {object} BrowserRefreshResponse
 * @property {number|string} StatusCode response status
 * @property {string} [Message] response message
 * @property {BrowserAccessToken} Result typed token result
 */

/**
 * Refresh endpoint marker consumed by the auth interceptor.
 * @type {string}
 */
export const BROWSER_REFRESH_PATH = '/auth/refresh';

/**
 * Bounds a refresh request so bootstrap can enter degraded recovery.
 * @type {number}
 */
export const BROWSER_REFRESH_TIMEOUT_MS = 30000;

/**
 * Anonymous liveness endpoint used after refresh recovery is exhausted.
 * @type {string}
 */
export const BROWSER_LIVENESS_PATH = '/health';

/**
 * Current-user endpoint used to populate browser identity after refresh.
 * @type {string}
 */
export const BROWSER_ME_PATH = '/auth/me';

/**
 * Current-user avatar. Identity comes from the access token, same as `/auth/me`.
 * @type {string}
 */
export const BROWSER_ME_AVATAR_PATH = '/auth/me/avatar';

/**
 * Browser logout endpoint that revokes the refresh session and clears the cookie.
 * @type {string}
 */
export const BROWSER_LOGOUT_PATH = '/auth/logout';

/**
 * Exchanges the HttpOnly browser refresh session for an access token.
 * The refresh cookie is sent by the browser; no credential is readable here.
 * @returns {Promise<BrowserAccessToken>}
 */
export const refreshBrowserAccessToken = async () => {
  /** @type {import('axios').AxiosRequestConfig & {_authRefresh: boolean}} */
  const requestConfig = {
    method: 'POST',
    url: BROWSER_REFRESH_PATH,
    withCredentials: true,
    timeout: BROWSER_REFRESH_TIMEOUT_MS,
    _authRefresh: true,
    headers: {
      Authorization: undefined,
    },
  };
  const response = /** @type {{data?: BrowserRefreshResponse}} */ (
    await externalApi(requestConfig)
  );
  const result = response?.data?.Result;
  const accessToken = result?.accessToken;

  if (!result || typeof accessToken !== 'string' || accessToken.length === 0) {
    throw new Error('Refresh response did not contain an access token');
  }

  return result;
};

/**
 * Checks remote API availability without requiring an access token.
 * @returns {Promise<unknown>}
 */
export const probeBrowserLiveness = async () => {
  const requestConfig = /** @type {import('axios').AxiosRequestConfig & {
    _authRefresh: boolean;
  }} */ ({
    method: 'GET',
    url: BROWSER_LIVENESS_PATH,
    withCredentials: true,
    _authRefresh: true,
  });
  const response = await externalApi(requestConfig);
  return response?.data;
};

/**
 * Loads the authenticated browser user for Redux identity state.
 * `/auth/me` returns `UserResponseModel` with PascalCase fields (`Id`, `FirstName`, …).
 * @param {string} accessToken current browser access token
 * @returns {Promise<import('../../../global').UserDataResponse>}
 */
export const fetchCurrentBrowserUser = async (accessToken) => {
  if (typeof accessToken !== 'string' || accessToken.length === 0) {
    throw new Error('Current user request requires an access token');
  }

  /** @type {import('axios').AxiosRequestConfig & {_authKind: 'authenticated'}} */
  const requestConfig = {
    method: 'GET',
    url: BROWSER_ME_PATH,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    _authKind: 'authenticated',
  };
  const response = await externalApi(requestConfig);
  const user = /** @type {import('../../../global').UserDataResponse} */ (
    response?.data?.Result
  );
  if (!user || typeof user.Id !== 'number' || user.Id <= 0) {
    throw new Error('Current user response did not contain a user');
  }

  return user;
};

/**
 * Loads raw avatar bytes for the authenticated browser user.
 * The route uses the access token, not a user id.
 * @param {string} accessToken current browser access token
 * @returns {Promise<Blob>}
 */
export const fetchCurrentUserAvatar = async (accessToken) => {
  if (typeof accessToken !== 'string' || accessToken.length === 0) {
    throw new Error('Avatar request requires an access token');
  }

  /** @type {import('axios').AxiosRequestConfig & {_authKind: 'authenticated'}} */
  const requestConfig = {
    method: 'GET',
    url: BROWSER_ME_AVATAR_PATH,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    responseType: 'blob',
    _authKind: 'authenticated',
  };
  const response = await externalApi(requestConfig);
  return response?.data;
};

/**
 * Revokes the current browser refresh session and asks the API to clear the cookie.
 * @param {string} accessToken current browser access token
 * @returns {Promise<unknown>}
 */
export const logoutBrowserSession = async (accessToken) => {
  if (typeof accessToken !== 'string' || accessToken.length === 0) {
    throw new Error('Logout requires an access token');
  }

  /** @type {import('axios').AxiosRequestConfig & {
    _authKind: 'authenticated';
    _authRefresh?: boolean;
  }} */
  const requestConfig = {
    method: 'POST',
    url: BROWSER_LOGOUT_PATH,
    withCredentials: true,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    _authKind: 'authenticated',
  };
  const response = await externalApi(requestConfig);
  return response?.data;
};
